// utilities/orderDeliverySteps.js
// ─────────────────────────────────────────────────────────────────────────────
// SINGLE SOURCE OF TRUTH for "where is this delivery, and what does the vendor
// do next?".
//
// WHY THIS EXISTS (Sept-2026 vendor order-details walkthrough)
// -----------------------------------------------------------
// `delivery_status` on models/Orders.js has TEN members:
//
//   PROCESSING, PENDING_3PL, ASSIGNED, ACCEPTED, PICKED_UP,
//   DISPATCHED, IN_TRANSIT, ARRIVED, DELIVERED, REJECTED
//
// (plus FULFILLED / NOT_APPLICABLE on the counter-sale enum).
//
// Every vendor order-details page treated it as a four-rung ladder
// (PROCESSING → ASSIGNED → DISPATCHED → DELIVERED) and fell through to a
// `default` for everything else. Six of the ten statuses therefore scored 0%,
// and those six are precisely the ones the VENDOR'S PAGE never writes:
//
//   ACCEPTED, PICKED_UP        the in-house rider app
//                              (Lusuku_Rider_IOS OrderDetailsScreen.js sends
//                              ACCEPTED, DispatchOrderScreen.js PICKED_UP)
//   PENDING_3PL                3PL assignment + autoAssignmentService
//   IN_TRANSIT, ARRIVED        the 3PL rail (threePLSocketHandler)
//   FULFILLED                  counter sales / ticket fulfilment
//
// The server accepts any enum member from the rider endpoint —
// `rejectBadDeliveryStatus` validates against the schema enum and nothing
// narrower — so all six really do land on live orders. The moment one did, the
// vendor's progress bar emptied, every step dot went grey, and "Dispatch
// Order" (hard-gated on `delivery_status === "ASSIGNED"`) went dead. The order
// was progressing normally; the vendor's screen said it had gone backwards and
// offered them nothing to press.
//
// So the ladder lives here once, and every surface reads stage, percentage and
// next-step copy from the same table.
//
// NOTE ON DISPATCHED vs PICKED_UP: these are PEERS, not neighbours. The vendor
// presses "Dispatch Order" (writes DISPATCHED); the rider marks 'picked_up'
// (writes PICKED_UP). Either can be the first status after ACCEPTED, and an
// order can reach the customer without ever being DISPATCHED. Neither may be
// treated as a precondition of the other.
// ─────────────────────────────────────────────────────────────────────────────

/** The four rungs the tracker draws. Index is the stage number. */
export const DELIVERY_STAGES = [
  { key: "PROCESSING", label: "Processing" },
  { key: "ASSIGNED", label: "Assigned" },
  { key: "DISPATCHED", label: "Dispatched" },
  { key: "DELIVERED", label: "Delivered" },
];

/** Who the order is waiting on. Drives the banner's colour and wording. */
export const WAITING_ON = {
  VENDOR: "VENDOR",
  RIDER: "RIDER",
  PARTNER: "PARTNER",
  NOBODY: "NOBODY",
};

// Mirrors the server dispatch gate in utils/paymentConfirmation.js. COD is
// deliberately included: cash is collected on delivery, so a COD order IS
// dispatchable while still showing "Unpaid".
const CONFIRMED_PAYMENT_STATUSES = new Set([
  "COD",
  "CONFIRMED",
  "COMPLETED",
  "PAID",
]);

const statusIsConfirmed = (value) => {
  const s = String(value || "")
    .trim()
    .toUpperCase();
  return s.length > 0 && CONFIRMED_PAYMENT_STATUSES.has(s);
};

// Methods that are dispatchable while still unpaid. COD is collected at the
// door; CASH is a counter sale whose money the vendor already holds.
//
// WHY paymentMethod AND paymentStatus: the server gate reads paymentStatus,
// and `paymentStatus: "COD"` is what the PACKAGE path stores. The ordinary
// order path (orderController's checkout, which is what a restaurant order
// goes through) identifies cash-on-delivery by `paymentMethod === "COD"` and
// may leave paymentStatus unset — the order in front of a restaurant vendor
// typically shows paymentMethod COD with no paymentStatus at all. Reading only
// paymentStatus here would call every ordinary COD order unpaid and warn the
// vendor about a payment problem that does not exist.
const UNPAID_DISPATCHABLE_METHODS = new Set(["COD", "CASH"]);

const methodIsCollectOnDelivery = (value) =>
  UNPAID_DISPATCHABLE_METHODS.has(
    String(value || "")
      .trim()
      .toUpperCase()
  );

/**
 * May this order legally move to a fulfilment state? Frontend mirror of
 * `isOrderPaymentConfirmed` (utils/paymentConfirmation.js), widened to the
 * paymentMethod spelling above. Used ONLY to warn the vendor before they press
 * a button the server would answer with 409 PAYMENT_NOT_CONFIRMED — never to
 * block the button itself. The server owns the gate; a frontend guess that
 * goes the other way strands a vendor with no control, which is the exact
 * failure this module exists to remove.
 */
export const isDispatchPaymentConfirmed = (order) =>
  order?.isPaid === true ||
  statusIsConfirmed(order?.paymentStatus) ||
  statusIsConfirmed(order?.payment_status) ||
  methodIsCollectOnDelivery(order?.paymentMethod);

// ─────────────────────────────────────────────────────────────────────────────
// The ladder. `pct` positions the filled track; it rises monotonically within a
// stage so a rider's progress is visible even though the tracker draws only
// four dots.
// ─────────────────────────────────────────────────────────────────────────────
const STEPS = {
  PROCESSING: {
    stage: 0,
    pct: 20,
    waitingOn: WAITING_ON.VENDOR,
    title: "Prepare the order, then assign a delivery",
    detail:
      "When the food is packed and ready, pick one of your riders below, or send it to a 3PL partner. This is the last stage at which the order can be cancelled.",
  },
  PENDING_3PL: {
    stage: 1,
    pct: 34,
    waitingOn: WAITING_ON.PARTNER,
    title: "Waiting for the 3PL partner to accept",
    detail:
      "The partner has the request. If nobody picks it up, recall the order and assign one of your own riders instead.",
  },
  ASSIGNED: {
    stage: 1,
    pct: 45,
    waitingOn: WAITING_ON.RIDER,
    title: "Waiting for the rider to accept",
    detail:
      "Hand the order over when the rider reaches you, then press Dispatch Order. If the rider declines, the order returns to Processing and you assign again.",
  },
  ACCEPTED: {
    stage: 1,
    pct: 56,
    waitingOn: WAITING_ON.RIDER,
    title: "Rider accepted and is on the way to you",
    detail:
      "Have the order packed and waiting. Press Dispatch Order once it is in the rider's hands.",
  },
  DISPATCHED: {
    stage: 2,
    pct: 70,
    waitingOn: WAITING_ON.RIDER,
    title: "On the way to the customer",
    detail:
      "The order has left you. The rider closes it out on delivery; you only need to mark it delivered here if the rider cannot.",
  },
  PICKED_UP: {
    stage: 2,
    pct: 76,
    waitingOn: WAITING_ON.RIDER,
    title: "Rider has collected the order",
    detail:
      "Nothing to do here. The rider moves it through to delivery from their app.",
  },
  IN_TRANSIT: {
    stage: 2,
    pct: 85,
    waitingOn: WAITING_ON.RIDER,
    title: "Rider is on the road to the customer",
    detail:
      "Track the rider on the map if the customer calls to ask. No action needed from you.",
  },
  ARRIVED: {
    stage: 2,
    pct: 93,
    waitingOn: WAITING_ON.RIDER,
    title: "Rider has reached the customer",
    detail:
      "Hand-over is happening now. On a cash order the rider collects the money at this point.",
  },
  DELIVERED: {
    stage: 3,
    pct: 100,
    waitingOn: WAITING_ON.NOBODY,
    title: "Delivered",
    detail: "This order is complete.",
  },
  FULFILLED: {
    stage: 3,
    pct: 100,
    waitingOn: WAITING_ON.NOBODY,
    title: "Fulfilled at the counter",
    detail: "This order was handed over in person. There is no delivery leg.",
  },
  REJECTED: {
    stage: -1,
    pct: 0,
    waitingOn: WAITING_ON.NOBODY,
    title: "Order cancelled",
    detail:
      "This order was cancelled or rejected and will not be delivered. The reason is recorded below.",
  },
  NOT_APPLICABLE: {
    stage: -1,
    pct: 0,
    waitingOn: WAITING_ON.NOBODY,
    title: "No delivery on this order",
    detail: "This order is not being delivered, so there are no delivery steps.",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// AUDIENCES. The ladder — which rung, what percentage, who holds the order — is
// the same for everyone. Only the sentences change.
//
// The vendor is working the order, so their copy is instructions ("press
// Dispatch Order"). An admin is watching someone else work it, so the same
// state has to be reported rather than commanded: "Your turn" is wrong on an
// admin screen, and telling an admin to press a button the VENDOR owns sends
// them to intervene in a queue that is running normally.
//
// Only the differing statuses are listed; anything absent falls back to the
// vendor sentence, which reads correctly for both.
// ─────────────────────────────────────────────────────────────────────────────
export const AUDIENCE = { VENDOR: "VENDOR", ADMIN: "ADMIN" };

const ADMIN_COPY = {
  PROCESSING: {
    title: "Vendor is preparing the order",
    detail:
      "The vendor assigns a rider or a 3PL partner once it is packed. Nothing to do here unless it has been sitting too long.",
  },
  PENDING_3PL: {
    title: "Waiting for the 3PL partner to accept",
    detail:
      "The request is out to the partner. If nobody takes it, the vendor can recall it and use one of their own riders.",
  },
  ASSIGNED: {
    title: "Waiting for the rider to accept",
    detail:
      "If the rider declines, the order drops back to Processing and the vendor assigns again.",
  },
  ACCEPTED: {
    title: "Rider accepted and is heading to the vendor",
    detail: "The vendor dispatches once the order is in the rider's hands.",
  },
  DISPATCHED: {
    title: "On the way to the customer",
    detail: "The rider closes it out on delivery.",
  },
  PICKED_UP: {
    title: "Rider has collected the order",
    detail: "The rider moves it through to delivery from their app.",
  },
  IN_TRANSIT: {
    title: "Rider is on the road to the customer",
    detail: "Track the rider on the map if the customer or the vendor calls.",
  },
  ARRIVED: {
    title: "Rider has reached the customer",
    detail:
      "Hand-over is happening now. On a cash order the rider collects the money at this point.",
  },
  DELIVERED: { title: "Delivered", detail: "This order is complete." },
};

const ADMIN_PAYMENT_BLOCK = {
  title: "Payment not recorded, so the vendor cannot dispatch",
  detail:
    "This order is not cash-on-delivery and no payment has been recorded, so the server will refuse to dispatch it. It needs a payment against it, or a manual mark-as-paid.",
};

const ADMIN_DELIVERED_UNPAID =
  "Delivered but never marked paid. On a cash order this is money the rider still owes in.";

const UNKNOWN_STEP = {
  stage: 0,
  pct: 0,
  waitingOn: WAITING_ON.VENDOR,
  title: "Awaiting the next update",
  detail:
    "This order is not on a recognised delivery status yet. Refresh in a moment, and contact support if it stays here.",
};

/**
 * The rung this order sits on, plus the copy that tells the vendor what to do.
 * Always returns an object; an unrecognised status yields UNKNOWN_STEP rather
 * than collapsing the tracker to zero.
 *
 * @param {object} order
 * @param {object} [opts]
 * @param {string} [opts.audience] AUDIENCE.VENDOR (default) or AUDIENCE.ADMIN
 * @returns {{status:string, stage:number, pct:number, waitingOn:string,
 *            title:string, detail:string, isTerminal:boolean,
 *            isCancelled:boolean, paymentBlocks:boolean, audience:string}}
 */
export const getDeliveryStep = (order, { audience = AUDIENCE.VENDOR } = {}) => {
  const forAdmin = audience === AUDIENCE.ADMIN;
  const status = String(order?.delivery_status || "").toUpperCase();
  const base = STEPS[status] || UNKNOWN_STEP;

  // The server refuses DISPATCHED/PICKED_UP/IN_TRANSIT/ARRIVED/DELIVERED with
  // 409 PAYMENT_NOT_CONFIRMED unless payment is confirmed (COD counts). Warn
  // while the order is still in the vendor's hands, not after they press.
  const beforeFulfilment = base.stage >= 0 && base.stage <= 1;
  // `order` is undefined while the details request is in flight; do not accuse
  // a not-yet-loaded order of a payment problem.
  const paymentBlocks =
    Boolean(order) && beforeFulfilment && !isDispatchPaymentConfirmed(order);

  const step = {
    status,
    ...base,
    ...(forAdmin && ADMIN_COPY[status] ? ADMIN_COPY[status] : {}),
    paymentBlocks,
    audience,
  };

  if (paymentBlocks) {
    if (forAdmin) {
      Object.assign(step, ADMIN_PAYMENT_BLOCK);
    } else {
      step.title = "Record the payment before dispatching";
      step.detail =
        "This order is not cash-on-delivery and no payment has been recorded, so it cannot be dispatched yet. Take payment or have it marked as paid, then continue with the delivery.";
    }
    step.waitingOn = WAITING_ON.VENDOR;
  }

  // A delivered COD order still needs the cash accounted for.
  if (status === "DELIVERED" && !order?.isPaid) {
    step.detail = forAdmin
      ? ADMIN_DELIVERED_UNPAID
      : "This order was never marked paid, so confirm the cash reached you before you close the day.";
  }

  step.isCancelled = status === "REJECTED";
  step.isTerminal = step.stage === 3 || step.isCancelled;
  return step;
};

/** Filled-track percentage. Replaces the old four-case getProgress(). */
export const getDeliveryProgress = (order) => getDeliveryStep(order).pct;

/**
 * Statuses at which the vendor's "Dispatch Order" button does something.
 * ASSIGNED and ACCEPTED both qualify: a rider who has accepted has not yet
 * been handed the food, so the vendor still has a dispatch to record.
 */
export const canVendorDispatch = (order) =>
  ["ASSIGNED", "ACCEPTED"].includes(
    String(order?.delivery_status || "").toUpperCase()
  );

/**
 * Statuses at which "Mark as Delivered" does something. Everything from the
 * moment the order leaves the vendor up to (but not including) DELIVERED, so a
 * vendor is never stranded by a rider who stops updating their app.
 */
export const canVendorMarkDelivered = (order) =>
  ["DISPATCHED", "PICKED_UP", "IN_TRANSIT", "ARRIVED"].includes(
    String(order?.delivery_status || "").toUpperCase()
  );

export default getDeliveryStep;
